import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLocalization } from "@/context/localization-context";
import { catalogWrites, classifyWriteError, useCatalogWrite, type WriteError } from "../api";
import { RECORD_ID, slug } from "../model";
import type { CatalogProduct, CatalogRecipe, ProductDraft, ProductKind } from "../types";
import { EditorFooter, Field } from "./shared";

const NO_RECIPE = "__none__";

/** Creates a product (``product`` null) or edits one: what the kitchen sells and the recipe a pizza is checked against. */
export function ProductEditor({
  open,
  onOpenChange,
  product,
  products,
  recipes,
  onReload,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: CatalogProduct | null;
  products: CatalogProduct[];
  recipes: CatalogRecipe[];
  onReload: () => Promise<unknown>;
}) {
  const { t } = useLocalization();
  const write = useCatalogWrite();
  const isNew = product === null;

  const [id, setId] = useState("");
  const [idTouched, setIdTouched] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<ProductKind>("pizza");
  const [recipeId, setRecipeId] = useState<string | null>(null);
  const [base, setBase] = useState<string | null>(null);
  const [error, setError] = useState<WriteError | null>(null);
  const [resetPending, setResetPending] = useState(false);

  const reset = (from: CatalogProduct | null) => {
    setId(from?.id ?? "");
    setIdTouched(false);
    setName(from?.name ?? "");
    setKind(from?.kind ?? "pizza");
    setRecipeId(from?.recipe_id ?? null);
    setBase(from?.updated_at ?? null);
    setError(null);
  };

  useEffect(() => {
    if (open) reset(product);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (resetPending && product && (product.updated_at ?? null) !== base) {
      reset(product);
      setResetPending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, resetPending]);

  const sortedRecipes = useMemo(() => [...recipes].sort((a, b) => a.name.localeCompare(b.name)), [recipes]);
  const draft: ProductDraft = { name: name.trim(), kind, recipe_id: recipeId };
  const effectiveId = isNew ? id.trim() : product.id;
  const idProblem = !isNew
    ? null
    : !effectiveId
      ? t("catalog.product.idMissing")
      : !RECORD_ID.test(effectiveId)
        ? t("catalog.product.idInvalid")
        : products.some((p) => p.id === effectiveId)
          ? t("catalog.product.idTaken")
          : null;
  const unchanged =
    !isNew && draft.name === product.name && draft.kind === product.kind && (draft.recipe_id ?? null) === (product.recipe_id ?? null);
  const canSave = !!draft.name && !idProblem && (kind !== "pizza" || !!recipeId) && !unchanged;

  const save = async () => {
    setError(null);
    try {
      await write.mutateAsync(
        isNew ? catalogWrites.createProduct(effectiveId, draft) : catalogWrites.updateProduct(product.id, draft, base),
      );
      toast.success(t("catalog.product.saved", { name: draft.name }));
      onOpenChange(false);
    } catch (e) {
      setError(classifyWriteError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !write.isPending && onOpenChange(next)}>
      <DialogContent className="max-w-xl gap-5">
        <DialogHeader className="text-left">
          <DialogTitle className="pr-6">
            {isNew ? t("catalog.product.newTitle") : t("catalog.product.editTitle", { name: product.name })}
          </DialogTitle>
          <DialogDescription>{t("catalog.product.description")}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_11rem]">
          <Field label={t("catalog.product.name")} htmlFor="product-name">
            <Input
              id="product-name"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                if (isNew && !idTouched) setId(slug(event.target.value));
              }}
              autoComplete="off"
            />
          </Field>
          <Field label={t("catalog.product.id")} htmlFor="product-id" hint={isNew ? idProblem ?? undefined : undefined}>
            <Input
              id="product-id"
              value={isNew ? id : product.id}
              disabled={!isNew}
              onChange={(event) => {
                setId(event.target.value);
                setIdTouched(true);
              }}
              className="font-mono"
              autoComplete="off"
            />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-[10rem_minmax(0,1fr)]">
          <Field label={t("catalog.product.kind")}>
            <Select
              value={kind}
              onValueChange={(value) => {
                setKind(value as ProductKind);
                if (value === "other") setRecipeId(null);
              }}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pizza">{t("catalog.product.kind.pizza")}</SelectItem>
                <SelectItem value="other">{t("catalog.product.kind.other")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("catalog.product.recipe")} hint={kind === "pizza" && !recipeId ? t("catalog.product.recipeMissing") : undefined}>
            <Select
              value={recipeId ?? NO_RECIPE}
              onValueChange={(value) => setRecipeId(value === NO_RECIPE ? null : value)}
              disabled={kind !== "pizza"}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_RECIPE}>{t("catalog.product.noRecipe")}</SelectItem>
                {sortedRecipes.map((recipe) => (
                  <SelectItem key={recipe.id} value={recipe.id}>
                    {recipe.name} <span className="font-mono text-muted-foreground">({recipe.id})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <EditorFooter
          error={error}
          onReload={() => {
            setResetPending(true);
            void onReload();
          }}
          saving={write.isPending}
          canSave={canSave}
          onCancel={() => onOpenChange(false)}
          onSave={() => void save()}
        />
      </DialogContent>
    </Dialog>
  );
}
